import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { eur, dateFr, LIBRARY_ITEM_TYPES, UNITS, TVA_RATES, PRICING_MODES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Pencil, Trash2, Star, History, Copy } from "lucide-react";
import PacksManager from "@/components/PacksManager";

const empty = {
  category: "", subcategory: "", designation: "", description: "", brand: "", model: "",
  reference: "", unit: "U", purchase_price: 0, sale_price: 0, pricing_mode: "fixe",
  item_type: "matériel", tva_rate: 0, source: "", favorite: false, active: true,
};

export default function LibraryPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState({});
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [favOnly, setFavOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [history, setHistory] = useState(null);
  const [view, setView] = useState("items");

  const load = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    if (catFilter !== "all") params.category = catFilter;
    if (favOnly) params.favorite = true;
    const { data } = await api.get("/library", { params });
    setItems(data);
  }, [search, catFilter, favOnly]);

  useEffect(() => { api.get("/library/categories").then((r) => setCategories(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ ...empty, ...it }); setEditId(it.id); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setNum = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.designation.trim()) { toast.error("La désignation est obligatoire"); return; }
    const payload = {
      ...form,
      purchase_price: Number(form.purchase_price || 0),
      sale_price: Number(form.sale_price || 0),
      tva_rate: Number(form.tva_rate || 0),
    };
    delete payload.id; delete payload.created_at; delete payload.marge;
    delete payload.usage_count; delete payload.last_used_at; delete payload.price_history;
    try {
      if (editId) await api.put(`/library/${editId}`, payload);
      else await api.post("/library", payload);
      toast.success(editId ? "Élément mis à jour" : "Élément ajouté à la bibliothèque");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleFav = async (it) => {
    await api.put(`/library/${it.id}`, { ...it, favorite: !it.favorite });
    load();
  };

  const remove = async () => {
    try { await api.delete(`/library/${deleteId}`); toast.success("Élément supprimé"); setDeleteId(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const duplicate = async (it) => {
    const payload = { ...empty, ...it, designation: `${it.designation} (copie)`, reference: "", favorite: false };
    delete payload.id; delete payload.created_at; delete payload.marge;
    delete payload.usage_count; delete payload.last_used_at; delete payload.price_history;
    try { await api.post("/library", payload); toast.success("Élément dupliqué"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const importBpu = async () => {
    try {
      const { data } = await api.post("/library/import-bpu");
      toast.success(`BPU importé — ${data.created} créé(s), ${data.updated} mis à jour, ${data.skipped} doublon(s) ignoré(s)`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const showHistory = async (it) => {
    const { data } = await api.get(`/library/${it.id}`);
    setHistory({ designation: it.designation, entries: data.price_history || [] });
  };

  const marge = (Number(form.sale_price || 0) - Number(form.purchase_price || 0));

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Bibliothèque</h1>
          <p className="text-slate-600">{view === "items" ? `${items.length} prestation(s) / produit(s)` : "Packs / prestations composées"}</p>
        </div>
        {view === "items" && (
          <div className="flex gap-2">
            <Button data-testid="import-bpu-button" onClick={importBpu} variant="outline" className="h-12 font-bold border-[#0284C7] text-[#0284C7]">
              Réimporter le BPU
            </Button>
            <Button data-testid="add-library-button" onClick={openNew} className="h-12 font-bold bg-[#16A34A] hover:bg-[#15803D]">
              <Plus size={20} className="mr-1" /> Nouvel élément
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button data-testid="lib-tab-items" onClick={() => setView("items")} className={`h-11 px-5 rounded-md font-semibold text-sm ${view === "items" ? "bg-[#0284C7] text-white" : "bg-white border border-slate-200 text-slate-700"}`}>Éléments</button>
        <button data-testid="lib-tab-packs" onClick={() => setView("packs")} className={`h-11 px-5 rounded-md font-semibold text-sm ${view === "packs" ? "bg-[#0284C7] text-white" : "bg-white border border-slate-200 text-slate-700"}`}>Packs</button>
      </div>

      {view === "packs" ? <PacksManager /> : (
      <>
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="library-search-input" placeholder="Rechercher (désignation, marque, modèle, catégorie…)" className="h-12 pl-10 bg-white" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger data-testid="library-category-filter" className="h-12 md:w-64 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les catégories</SelectItem>
            {Object.keys(categories).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <button data-testid="library-fav-filter" onClick={() => setFavOnly((v) => !v)} className={`h-12 px-4 rounded-md font-semibold border flex items-center gap-2 ${favOnly ? "bg-amber-100 border-amber-300 text-amber-700" : "bg-white border-slate-200 text-slate-700"}`}>
          <Star size={18} className={favOnly ? "fill-amber-500 text-amber-500" : ""} /> Favoris
        </button>
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="text-left p-3 w-8"></th>
              <th className="text-left p-3">Réf.</th>
              <th className="text-left p-3">Désignation</th>
              <th className="text-left p-3">Catégorie</th>
              <th className="text-left p-3">Type</th>
              <th className="text-right p-3">Achat</th>
              <th className="text-right p-3">Vente</th>
              <th className="text-right p-3">Marge</th>
              <th className="text-center p-3">Utilis.</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-slate-500">Aucun élément.</td></tr>}
            {items.map((it) => (
              <tr key={it.id} data-testid={`library-row-${it.id}`} className={`border-t border-slate-100 ${!it.active ? "opacity-50" : ""}`}>
                <td className="p-3">
                  <button data-testid={`fav-toggle-${it.id}`} onClick={() => toggleFav(it)}>
                    <Star size={18} className={it.favorite ? "fill-amber-500 text-amber-500" : "text-slate-300"} />
                  </button>
                </td>
                <td className="p-3"><span className="text-xs font-semibold text-[#0284C7]">{it.reference || "—"}</span></td>
                <td className="p-3">
                  <div className="font-semibold text-slate-900">{it.designation}</div>
                  {(it.brand || it.model) && <div className="text-xs text-slate-500">{[it.brand, it.model].filter(Boolean).join(" · ")}</div>}
                  {it.pricing_mode && it.pricing_mode !== "fixe" && <span className="text-[10px] font-semibold text-amber-600">{PRICING_MODES[it.pricing_mode]?.label}</span>}
                </td>
                <td className="p-3 text-slate-600">{it.category}{it.subcategory ? ` / ${it.subcategory}` : ""}</td>
                <td className="p-3 text-slate-600">{it.item_type}</td>
                <td className="p-3 text-right text-slate-600">{eur(it.purchase_price)}</td>
                <td className="p-3 text-right font-semibold text-slate-900">{it.pricing_mode === "sur_devis" ? <span className="text-amber-600">Sur devis</span> : <>{eur(it.sale_price)}<span className="text-xs text-slate-400"> /{it.unit}</span></>}</td>
                <td className={`p-3 text-right font-semibold ${it.marge >= 0 ? "text-[#16A34A]" : "text-red-600"}`}>{eur(it.marge)}</td>
                <td className="p-3 text-center text-slate-500">{it.usage_count || 0}</td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" data-testid={`history-${it.id}`} onClick={() => showHistory(it)} className="h-9 w-9"><History size={16} /></Button>
                    <Button size="icon" variant="ghost" data-testid={`dup-library-${it.id}`} onClick={() => duplicate(it)} className="h-9 w-9 text-slate-600"><Copy size={16} /></Button>
                    <Button size="icon" variant="ghost" data-testid={`edit-library-${it.id}`} onClick={() => openEdit(it)} className="h-9 w-9 text-[#0284C7]"><Pencil size={16} /></Button>
                    <Button size="icon" variant="ghost" data-testid={`delete-library-${it.id}`} onClick={() => setDeleteId(it.id)} className="h-9 w-9 text-red-600"><Trash2 size={16} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-3">
        {items.map((it) => (
          <div key={it.id} data-testid={`library-card-${it.id}`} className={`bg-white border border-slate-200 rounded-md p-4 ${!it.active ? "opacity-50" : ""}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold text-slate-900">{it.designation}</div>
                <div className="text-xs text-slate-500">{it.category}{it.subcategory ? ` / ${it.subcategory}` : ""} · {it.item_type}</div>
              </div>
              <button data-testid={`fav-toggle-mobile-${it.id}`} onClick={() => toggleFav(it)}><Star size={20} className={it.favorite ? "fill-amber-500 text-amber-500" : "text-slate-300"} /></button>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 text-sm">
              <span className="text-slate-500">Vente <b className="text-slate-900">{eur(it.sale_price)}/{it.unit}</b></span>
              <span className="text-slate-500">Marge <b className="text-[#16A34A]">{eur(it.marge)}</b></span>
              <span className="text-slate-500">Utilisé {it.usage_count || 0}×</span>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(it)} className="flex-1 border-[#0284C7] text-[#0284C7]"><Pencil size={15} className="mr-1" />Modifier</Button>
              <Button size="sm" variant="outline" onClick={() => showHistory(it)} className="border-slate-300"><History size={15} /></Button>
              <Button size="sm" variant="outline" onClick={() => setDeleteId(it.id)} className="border-red-300 text-red-600"><Trash2 size={15} /></Button>
            </div>
          </div>
        ))}
      </div>
      </>
      )}

      {/* Edit/create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Modifier l'élément" : "Nouvel élément"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Désignation *</Label>
              <Input data-testid="library-designation-input" className="h-12" value={form.designation} onChange={set("designation")} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={form.category || undefined} onValueChange={(v) => setForm((f) => ({ ...f, category: v, subcategory: "" }))}>
                  <SelectTrigger data-testid="library-category-select" className="h-12"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{Object.keys(categories).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sous-catégorie</Label>
                <Select value={form.subcategory || undefined} onValueChange={(v) => setForm((f) => ({ ...f, subcategory: v }))} disabled={!form.category}>
                  <SelectTrigger data-testid="library-subcategory-select" className="h-12"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{(categories[form.category] || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Référence (BPU)</Label><Input data-testid="library-reference-input" className="h-12" placeholder="Ex : CL01" value={form.reference || ""} onChange={set("reference")} /></div>
              <div className="space-y-2"><Label>Marque</Label><Input data-testid="library-brand-input" className="h-12" value={form.brand} onChange={set("brand")} /></div>
            </div>
            <div className="space-y-2"><Label>Modèle / Référence fabricant</Label><Input data-testid="library-model-input" className="h-12" value={form.model} onChange={set("model")} /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea data-testid="library-description-input" value={form.description} onChange={set("description")} /></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.item_type} onValueChange={(v) => setForm((f) => ({ ...f, item_type: v }))}>
                  <SelectTrigger data-testid="library-type-select" className="h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>{LIBRARY_ITEM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unité</Label>
                <Select value={form.unit} onValueChange={(v) => setForm((f) => ({ ...f, unit: v }))}>
                  <SelectTrigger data-testid="library-unit-select" className="h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mode de prix</Label>
                <Select value={form.pricing_mode || "fixe"} onValueChange={(v) => setForm((f) => ({ ...f, pricing_mode: v }))}>
                  <SelectTrigger data-testid="library-pricing-mode-select" className="h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PRICING_MODES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>TVA</Label>
                <Select value={String(form.tva_rate)} onValueChange={(v) => setForm((f) => ({ ...f, tva_rate: Number(v) }))}>
                  <SelectTrigger data-testid="library-tva-select" className="h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>{TVA_RATES.map((t) => <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 items-end">
              <div className="space-y-2"><Label>Prix d'achat (€)</Label><Input type="number" step="0.01" data-testid="library-purchase-input" className="h-12" value={form.purchase_price} onChange={setNum("purchase_price")} /></div>
              <div className="space-y-2"><Label>Prix de vente (€)</Label><Input type="number" step="0.01" data-testid="library-sale-input" className="h-12" value={form.sale_price} onChange={setNum("sale_price")} /></div>
              <div className="space-y-1">
                <Label>Marge</Label>
                <div data-testid="library-marge-display" className={`h-12 flex items-center px-3 rounded-md border font-bold ${marge >= 0 ? "text-[#16A34A] border-green-200 bg-green-50" : "text-red-600 border-red-200 bg-red-50"}`}>{eur(marge)}</div>
              </div>
            </div>
            <div className="flex items-center gap-6 pt-2">
              <label className="flex items-center gap-2 font-medium"><Switch data-testid="library-favorite-switch" checked={form.favorite} onCheckedChange={(v) => setForm((f) => ({ ...f, favorite: v }))} /> Favori</label>
              <label className="flex items-center gap-2 font-medium"><Switch data-testid="library-active-switch" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} /> Actif</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-12">Annuler</Button>
            <Button data-testid="save-library-button" onClick={save} className="h-12 font-bold bg-[#16A34A] hover:bg-[#15803D]">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price history dialog */}
      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">Historique des prix</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 -mt-2">{history?.designation}</p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(history?.entries || []).length === 0 && <p className="text-slate-500 text-sm">Aucun historique.</p>}
            {(history?.entries || []).slice().reverse().map((e, i) => (
              <div key={i} className="flex justify-between border-b border-slate-100 py-2 text-sm">
                <span className="text-slate-500">{dateFr(e.date)}</span>
                <span className="font-semibold text-slate-900">{eur(e.sale_price)}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet élément ?</AlertDialogTitle>
            <AlertDialogDescription>Les devis existants ne seront pas affectés.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-library" onClick={remove} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
